import * as fromActions from '../actions/involuntary-change.actions';
import * as fromReducer from '../reducers/involuntary-change.reducer';
import { ContractError } from './../../core/models/models';
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { Tab } from 'src/app/components/tabs/tabs.component';
import { DOCSADoc, DOCSARequest } from 'src/app/core/models/docsa.model';
import { MiniState, StepMiniState } from 'src/app/core/models/models';
import { SplitPnrRequest } from 'src/app/search-result/actions/pnr.actions';
import { ItineraryService } from 'src/app/search-result/services/itinerary.service';
import { FareFamiliesState } from 'src/app/core/models/fare-family.model';
import { EmergencyContactModel } from 'src/app/core/models/emergency-contact.model';
import { Router } from '@angular/router';
import { ReissueService } from 'src/app/reissue/services/reissue.service';
import { IBookingData } from '../components/confirmation-step/confirmation-step.component';
import { SettingsService } from 'src/app/settings/services/settings.service';
import { IClientSettings } from 'src/app/core/models/agency-settings.model';
import { VoluntaryChangeService } from 'src/app/voluntary-change/services/voluntary-change.service';
import { MapItineraryApi } from 'src/app/core/api/mappers/map-itinerary-api.service';
import { Price } from 'src/app/core/models/cancellation.model';
import {
  ExchangeOptionFilter,
  IAncillariesWaiveQuote,
  IAncillariesWaiveState,
  IInvoluntaryBookStepStates,
  InvoluntaryExchangeQuote
} from 'src/app/core/models/involuntary-change.model';
import {
  Flight,
  Itinerary,
  Location,
  Passenger,
  QuoteRequest,
  Segment,
  WaiverData
} from 'src/app/core/models/itinerary.model';
import {
  ExchangeAvailability,
  ExchangeAvailabilityOption,
  ExchangeSegment
} from 'src/app/core/models/voluntary-change.model';

@Injectable({
  providedIn: 'root'
})
export class InvoluntaryChangeService {
  clienSettings$: Observable<IClientSettings>;

  loadAlternativesTrigger = new Subject();
  hasSplit = false;

  constructor(
    private store: Store<fromReducer.InvoluntaryChangeState>,
    private itinerary: ItineraryService,
    private router: Router,
    private vcService: VoluntaryChangeService,
    private settingsService: SettingsService,
    private reissueService: ReissueService,
    private mapItineraryApi: MapItineraryApi
  ) {
    this.clienSettings$ = this.settingsService.getClientSettings();
  }

  dispatchIsAirlineOption(waiverData: WaiverData): void {
    this.store.dispatch(fromActions.ChooseArlineOption({ waiverData }));
  }

  dispatchClearInvoluntaryChange() {
    this.store.dispatch(fromActions.clearInvoluntaryChange());
  }

  dispatchValidateFlowPricing() {
    let settings: IClientSettings;
    this.clienSettings$.subscribe((settings) => {
      settings;
    });
    if (settings?.machineLearningEnabled) {
      this.dispatchCallML();
    } else {
      this.dispatchExchangePricing(false);
    }
  }

  dispatchExchangePricing(fromVoluntary: boolean): void {
    this.store.dispatch(fromActions.ExchangePricingRequest({ fromVoluntary }));
  }

  dispatchCallML(): void {
    this.store.dispatch(fromActions.MLSave());
  }

  dispatchChangeFlowToAutomatic(): void {
    this.store.dispatch(fromActions.ChangeToAutomatic());
  }

  dispatchInvolUpdatePricingQuoteRequestWithSeatPrices(reservationSeatPrices: Price[]) {
    this.store.dispatch(
      fromActions.UpdatePricingQuoteRequestWithSeatPrices({
        prices: reservationSeatPrices
      })
    );
  }

  getItinerary(): Observable<Itinerary> {
    return this.itinerary.getItinerary();
  }

  getExchangeSegments(): Observable<ExchangeSegment[]> {
    return this.store.select(fromReducer.exchangeSegments);
  }

  getOriginalItinerary(): Observable<Itinerary> {
    return this.store.pipe(select(fromReducer.selectOriginalItinerary));
  }

  getQuoteRequest(): Observable<QuoteRequest> {
    return this.store.pipe(select(fromReducer.selectQuoteRequest));
  }

  goTo(route: string): void {
    this.itinerary.navigateToWithParams(route);
  }

  getQuoteRequestForInvoluntary(): Observable<QuoteRequest> {
    return this.itinerary.getQuoteRequestForInvoluntary();
  }

  getCommission(): Observable<Number> {
    return this.store.pipe(select(fromReducer.selectCommission));
  }

  getIsNewFlow(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.selectIsNewFlow));
  }

  getInvoluntaryQuote(): Observable<InvoluntaryExchangeQuote> {
    return this.store.pipe(select(fromReducer.selectInvoluntaryQuote));
  }

  getNewSegments(): Observable<Segment[]> {
    return this.store.pipe(select(fromReducer.selectNewSegments));
  }

  getLoading(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.selectLoading));
  }

  getTabState(): Observable<any> {
    return this.store.pipe(select(fromReducer.selectTabState));
  }

  changeActiveTab(activeTab: number): void {
    this.store.dispatch(fromActions.ChangeActiveTab({ activeTab }));
  }

  addOptionSelected(selectedOption: ExchangeAvailabilityOption, index: number): void {
    this.store.dispatch(fromActions.AddSelectedOption({ selectedOption, index }));
  }

  getOptionSelected(): Observable<ExchangeAvailabilityOption> {
    return this.store.pipe(select(fromReducer.selectselectedOption));
  }

  getOptionExchangePricing(): Observable<ExchangeAvailabilityOption> {
    return this.store.pipe(select(fromReducer.selectExchangePricingOption));
  }

  getOptionSelectedSegments(): Observable<Segment[]> {
    return this.store.pipe(select(fromReducer.selectselectedOptionSegemnts));
  }

  getOptionSelectedSaved(): Observable<ExchangeAvailability[]> {
    return this.store.pipe(select(fromReducer.selectselectedOptionSaved));
  }

  getPersegmentSelected(): Observable<Segment[]> {
    return this.store.pipe(select(fromReducer.selectPerSegmentsSelected));
  }

  getPersegentSelected(): Observable<ExchangeAvailabilityOption> {
    return this.store.pipe(select(fromReducer.selectExchangePricing));
  }

  getSelectedFlightIndexes(): Observable<number[]> {
    return this.store.pipe(select(fromReducer.getSelectedFlightIndexes));
  }

  getAvailabilitiesState(): Observable<MiniState> {
    return this.store.pipe(select(fromReducer.selectAvailabilitiesState));
  }

  getAllClassesSelected(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.getAllClassesSelected));
  }

  getStateEmergencyContact(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.stateEmergencyContact));
  }

  getAvailabilitiesPerSegment(): Observable<ExchangeAvailability[]> {
    return this.store.pipe(select(fromReducer.selectAvailabilitiesPerSegment));
  }

  getReservationSeatPrices(): Observable<Price[]> {
    return this.store.pipe(select(fromReducer.selectReservationSeatPrices));
  }

  getOptionsPerSegment(): Observable<ExchangeAvailability[]> {
    return this.store.pipe(select(fromReducer.selectOptionsPerSegment));
  }

  getAvailabilitiesTabs(): Observable<Tab[]> {
    return this.store.pipe(select(fromReducer.selectAvailabilitiesTabs));
  }

  getFinalState(): Observable<MiniState> {
    return this.store.pipe(select(fromReducer.selectFinalState));
  }

  getSomePassengersHasAncilliary(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.selectSomePassengerHasAncilliary));
  }

  getStepState(): Observable<StepMiniState> {
    return this.store.pipe(select(fromReducer.selectStepState));
  }

  getExchangeOriginalRoute(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.getExchangeOriginalRoute));
  }

  getShowAncillariesModal(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.getShowAncillariesModal));
  }

  getAncillariesError(): Observable<ContractError> {
    return this.store.pipe(select(fromReducer.getAncillariesError));
  }

  getPassengersWithAncillaryValidation(): Observable<Passenger[][]> {
    return this.store.pipe(select(fromReducer.getPassengersWithAncillaryValidation));
  }

  getInvoluntaryBookStepStates(): Observable<IInvoluntaryBookStepStates[]> {
    return this.store.pipe(select(fromReducer.getInvoluntaryBookStepStates));
  }

  dispatchInvoluntaryQuoteSuccess(
    quote: InvoluntaryExchangeQuote,
    request: QuoteRequest,
    hasSplit: boolean
  ): void {
    this.hasSplit = hasSplit;
    this.clienSettings$
      .subscribe((settings) => {
        this.store.dispatch(
          fromActions.LoadInvoluntaryQuoteSuccess({ quote, request, hasSplit, settings })
        );
      })
      .unsubscribe();
  }

  dispatchLoadAvailabilities(exchangeSegments: ExchangeSegment[], isNewFlow: boolean): void {
    this.store.dispatch(fromActions.LoadingStart());
    this.store.dispatch(fromActions.LoadAvailabilitiesInvol({ exchangeSegments, isNewFlow }));
  }

  dispatchChooseOtherAlternative(): void {
    this.store.dispatch(fromActions.ChooseOtherAlternative());
  }

  dispatchGoToSeatmapNewFlow() {
    this.store.dispatch(fromActions.goToSeatmapNewFlow());
  }

  dispatchConfirmFare(): void {
    this.store.dispatch(fromActions.ConfirmFare());
  }

  dispatchBackToAlternatives(): void {
    this.store.dispatch(fromActions.BackToAlternatives());
  }

  dispatchBackToNewFlowStep(): void {
    this.store.dispatch(fromActions.BackToNewFlowStep());
  }

  dispatchLoadingStart(): void {
    this.store.dispatch(fromActions.LoadingStart());
  }

  dispatchLoadingStop(): void {
    this.store.dispatch(fromActions.loadingStop());
  }

  dispatchLoadAvailabilitesError(error: ContractError): void {
    this.store.dispatch(fromActions.LoadAvailabilitesError({ error }));
  }
  dispatchBackToAlternativesNewFlowStep(): void {
    this.store.dispatch(fromActions.BackToAlternativesNewFlowStep());
  }

  dispatchInvoluntaryBookRequest(data: IBookingData): void {
    this.store.dispatch(fromActions.ForceRefreshSesionAndBook({ data }));
  }

  dispatchExchangeBook(data: WaiverData): void {
    this.store.dispatch(fromActions.ExchangeBookRequest({ data }));
  }

  dispatchSaveFilter(filter: ExchangeOptionFilter, index: number): void {
    this.store.dispatch(fromActions.Filter({ filter, index }));
  }

  dispatchChangeAirlineRecommendation(addData: IBookingData, isFromVoluntary: boolean): void {
    this.store.dispatch(
      fromActions.RequestChangeAirlineRecommendation({ addData, isFromVoluntary })
    );
  }

  dispatchRequestSplit(quoteRequest: QuoteRequest) {
    this.store.dispatch(SplitPnrRequest({ quoteRequest }));
  }

  dispatchResetSplitState() {
    this.store.dispatch(fromActions.SplitPnrStateReset());
  }

  dispatchEndSplit() {
    this.store.dispatch(fromActions.EndSplit());
    this.vcService
      .getIsNewFlow()
      .subscribe((res) => {
        return res ? this.dispatchGoToSeatMap() : this.dispatchChooseAvailabilityCheckStep();
      })
      .unsubscribe();
  }

  getSplitState(): Observable<MiniState> {
    return this.store.pipe(select(fromReducer.selectSplitState));
  }

  getPricingError(): Observable<ContractError> {
    return this.store.pipe(select(fromReducer.selectPricingError));
  }

  getOverrideSegments(): Observable<number[]> {
    return this.itinerary.getOverrideSegments();
  }

  getIsReIssueRequired(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.isReIssueRequired));
  }

  getEmergencyContact(): Observable<EmergencyContactModel[]> {
    return this.store.pipe(select(fromReducer.getEmergencyContact));
  }

  setIsReIssueRequired(isReIssueRequired: boolean) {
    this.store.dispatch(fromActions.SetIsReIssueRequired({ isReIssueRequired }));
  }

  dispatchSaveDocsa(docs: DOCSADoc[]) {
    this.store.dispatch(fromActions.DocsaSave({ docs }));
  }

  dispatchSaveEmergencyContact(contact: EmergencyContactModel[]) {
    this.store.dispatch(fromActions.EmergencyContact({ contact }));
  }

  dispatchDocsaRequest() {
    this.store.dispatch(fromActions.DocsaRequest());
  }

  getDocsaRequest(): Observable<DOCSARequest> {
    return this.store.pipe(select(fromReducer.selectDocsaRequest));
  }

  getExchangePricing(): Observable<ExchangeAvailabilityOption> {
    return this.store.pipe(select(fromReducer.selectExchangePricing));
  }

  getDocsaDocs(): Observable<DOCSADoc[]> {
    return this.store.pipe(select(fromReducer.selectDocsaDocs));
  }

  getNeedsDocsa(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.selectNeedsDocsa));
  }

  getFareFamiliesState(): Observable<FareFamiliesState> {
    return this.store.pipe(select(fromReducer.getFareFamiliesState));
  }

  getExchangeAvailability(): Observable<any> {
    return this.store.pipe(select(fromReducer.getExchangeAvailability));
  }

  getAncillariesWaiveState(): Observable<IAncillariesWaiveState> {
    return this.store.pipe(select(fromReducer.getAncillariesWaiveState));
  }

  getReloadAvailabilities(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.getReloadAvailabilities));
  }

  dispatchRequireAncillariesWaive() {
    this.store.dispatch(fromActions.AncillariesWaiveRequired());
  }

  dispatchAncillariesWaive(quote: IAncillariesWaiveQuote) {
    this.store.dispatch(fromActions.AncillariesWaive({ quote }));
  }

  dispatchResetAncillariesWaiveState() {
    this.store.dispatch(fromActions.ResetAncillariesWaiveState());
  }

  getFarebasisFromSegment(segment: Segment | any): string[] {
    return segment.flights.reduce(
      (res: string[], flight: Flight) =>
        res.includes(flight.fareBasis) ? res : res.concat(flight.fareBasis),
      []
    );
  }

  dispatchLoadFareFamiliesInvoluntary(
    option: ExchangeAvailabilityOption,
    index: number,
    open = false
  ) {
    this.clienSettings$.subscribe((settings) => {
      if (settings?.showFamilyFareEnabled) {
        this.store.dispatch(fromActions.LoadFareFamiliesInvoluntary({ option, index, open }));
      }
    });
  }

  dispatchChooseAvailabilityCheckStep() {
    this.getOptionSelectedSaved()
      .subscribe((val) => {
        this.clienSettings$.subscribe((settings) => {
          if (settings.requestPassportInformation) {
            let res = val
              ? val.filter((item) => this.needDocsa(item.selectedOption.segments)).length
              : 0;
            res == 0
              ? this.dispatchGoToConfirmation()
              : this.store.dispatch(fromActions.GoToDocsa());
          } else if (settings.requestContactInformation) {
            let res = val
              ? val.filter((item) => this.needEmergencyContact(item.selectedOption.segments)).length
              : 0;
            res == 0
              ? this.dispatchSequenceAditionalInformationStep(false)
              : this.store.dispatch(fromActions.goToEmergencyContact());
          } else {
            !settings.requestPassportInformation && this.dispatchGoToConfirmation();
            !settings.requestContactInformation &&
              !this.hasSplit &&
              this.dispatchSequenceAditionalInformationStep(false);
          }
        });
      })
      .unsubscribe();
  }

  dispatchChooseAvailabilityCheckStepNewFlow() {
    this.store
      .select(fromReducer.getDataToCheckStep)
      .pipe(
        distinctUntilChanged((prev, current) => JSON.stringify(prev) === JSON.stringify(current))
      )
      .subscribe(({ options, isAirline, settings, isDocsRequired }) => {
        if (!isAirline) {
          if (isDocsRequired && settings?.requestPassportInformation) {
            this.store.dispatch(fromActions.GoToDocsa());
          } else {
            const docsaNeed = this.evaluateIfNeedDocsa(options);

            const nextStep = this.evaluateDocsaOrNextStep(docsaNeed, settings);

            nextStep
              ? this.store.dispatch(fromActions.GoToDocsa())
              : this.dispatchGoToConfirmationFromNewFlow();
          }
        } else {
          const emergencyContactNeed = this.evaluateNeedEmergencyContact(options);

          const nextStep = this.evaluateEmergencyOrNextStep(emergencyContactNeed, settings);

          nextStep
            ? this.store.dispatch(fromActions.goToEmergencyContact())
            : this.dispatchGoToConfirmationFromNewFlow();
        }
      });
  }

  evaluateIfNeedDocsa(options: ExchangeAvailability[]): number {
    let response = 0;
    if (options) {
      response = options.filter((item) => this.needDocsa(item.selectedOption.segments)).length;
    }
    return response;
  }

  evaluateDocsaOrNextStep(docsaNeed: number, settings: IClientSettings): boolean {
    let response = false;
    if (docsaNeed && settings?.requestPassportInformation) {
      response = true;
    }
    return response;
  }

  evaluateNeedEmergencyContact(options: ExchangeAvailability[]) {
    let response = 0;
    if (options) {
      response = options.filter((item) =>
        this.needEmergencyContact(item.selectedOption.segments)
      ).length;
    }
    return response;
  }

  evaluateEmergencyOrNextStep(emergencyContactNeed: number, settings: IClientSettings) {
    let response = false;
    if (emergencyContactNeed && settings?.requestContactInformation) {
      response = true;
    }
    return response;
  }

  returnToEmergencyContact() {
    this.getOptionSelectedSaved()
      .subscribe((val) => {
        this.clienSettings$.subscribe((settings) => {
          if (settings.requestPassportInformation) {
            let res = val
              ? val.filter((item) => this.needDocsa(item.selectedOption.segments)).length
              : 0;
            res == 0 ? this.dispatchReturnToStep1() : this.store.dispatch(fromActions.GoToDocsa());
          } else if (settings.requestContactInformation) {
            let res = val
              ? val.filter((item) => this.needEmergencyContact(item.selectedOption.segments)).length
              : 0;
            res == 0
              ? this.dispatchReturnToStep1()
              : this.store.dispatch(fromActions.goToEmergencyContact());
          } else {
            !settings.requestPassportInformation && this.dispatchReturnToStep1();
            !settings.requestContactInformation && this.dispatchReturnToStep1();
          }
        });
      })
      .unsubscribe();
  }

  dispatchSequenceAditionalInformationStep(newFlow: boolean) {
    this.getInvoluntaryQuote().subscribe((val) => {
      let res = val ? this.mapItineraryApi.needAditionalInformation(val.passenger) : false;
      !res
        ? newFlow
          ? this.dispatchGoToConfirmationFromNewFlow()
          : this.dispatchGoToConfirmation()
        : this.store.dispatch(fromActions.goToAditionalInformation());
    });
  }

  dispatchReturnFromAditionalInformationStep() {
    this.getOptionSelectedSaved()
      .subscribe((val) => {
        let res = val
          ? val.filter((item) => this.needEmergencyContact(item.selectedOption.segments)).length
          : 0;
        res == 0
          ? this.dispatchReturnToStep1()
          : this.store.dispatch(fromActions.goToEmergencyContact());
      })
      .unsubscribe();
  }

  dispatchCommission(value: number) {
    this.store.dispatch(fromActions.SaveCommission({ value }));
  }

  dispatchSaveIsNewFlow(value: boolean) {
    this.store.dispatch(fromActions.SaveIsNewFlow({ value }));
  }

  dispatchGoToAvailabilityExchangePricing() {
    this.store.dispatch(fromActions.ChooseExchangePricing());
  }

  dispatchGoToSeatMap() {
    this.store.dispatch(fromActions.GoToSeatMap());
  }

  dispatchGoToSplitStepNewFlow() {
    this.store.dispatch(fromActions.GoToSplitStepNewFlow());
  }

  dispatchGoToEmergencyContact() {
    this.store.dispatch(fromActions.goToEmergencyContact());
  }

  dispatchGoToConfirmation() {
    this.store.dispatch(fromActions.ChooseOtherAlternative());
  }

  dispatchGoToConfirmationFromNewFlow() {
    this.store.dispatch(fromActions.ChooseOtherAlternativeFromNewFlow());
  }

  dispatchReturnToStep1() {
    this.store.dispatch(fromActions.BackToAlternatives());
  }

  dispatchChangeSteps(isReIssueActive: boolean) {
    this.store.dispatch(fromActions.ChangeStepsNewFlow({ isReIssueActive }));
  }

  dispatchLoadAncillariesComparison(
    passengers: Passenger[],
    option: ExchangeAvailabilityOption
  ): void {
    this.store.dispatch(fromActions.LoadAncillariesComparison({ passengers, option }));
  }

  dispatchLoadAncillariesSegment(passengers: Passenger[]): void {
    this.store.dispatch(fromActions.LoadAncillariesSegments({ passengers }));
  }

  dispatchCloseAncillariesModal(): void {
    this.store.dispatch(fromActions.CloseAncillariesModal());
  }

  goToReissue(data?: WaiverData) {
    this.reissueService.dispatchSetBookingType(false, data);
    this.router.navigateByUrl('reissue');
  }

  private needDocsa(segments: Segment[]) {
    return segments.reduce(
      (res, seg) =>
        seg.flights.reduce(
          (re, flight) =>
            (this.isUSAorMexico(flight.arrivalLocation) &&
              !this.isSameCountry(flight.arrivalLocation, flight.departureLocation)) ||
            (this.isUSAorMexico(flight.departureLocation) &&
              !this.isSameCountry(flight.arrivalLocation, flight.departureLocation)) ||
            re,
          false
        ) || res,
      false
    );
  }

  private needEmergencyContact(segment: Segment[]) {
    return segment.reduce(
      (resp, seg) =>
        seg.flights.reduce(
          (resp1, flight) =>
            this.isUsa(flight.arrivalLocation) || this.isUsa(flight.departureLocation),
          false
        ) || resp,
      false
    );
  }

  private isUSAorMexico(location: Location): boolean {
    return location.country === 'MX' || location.country === 'US';
  }

  private isUsa(location: Location): boolean {
    return location.country === 'US';
  }

  private isSameCountry(location: Location, location2: Location): boolean {
    return (
      (location.country === 'US' && location2.country === 'US') ||
      (location.country === 'MX' && location2.country === 'MX')
    );
  }
}
